<template>
  <div class="column is-12" :class="classes">
    <div class="columns is-gapless is-vcentered is-border-bottom has-bg-light">
      <div class="column is-padding-left">
        <span class="title is-4 has-text-weight-medium">Lot n°{{lot.ref}}</span>
      </div>
      <div class="column is-narrow is-border-left" v-if="'documents' in lot && lot.documents && lot.documents.length && displayButton">
        <a class="button is-ghost has-text-grey" @click.prevent.stop="downloadFile(lot.documents, lot.name, '.zip')"
            :class="{'is-loading': downloadZipLoading}">
          <span class="icon is-medium is-size-4"><i class="fas fa-file-archive"></i></span>
          <span class="is-size-6">Télécharger les documents du lot</span>
        </a>
      </div>
      <div class="column is-narrow is-border-left" v-if="lot.plan && displayButton">
        <a class="button is-ghost has-text-grey" @click.prevent.stop="downloadFile(lot.plan, lot.ref, '.zip')"
            :class="{'is-loading': downloadZipLoading}">
          <span class="icon is-medium is-size-4"><i class="fas fa-file-pdf"></i></span>
          <span class="is-size-6">Télécharger le plan du lot</span>
        </a>
      </div>
      <div class="column is-narrow" v-if="isAdmin && displayButton">
        <router-link :to="{name: 'LotVue', params: {id: lot.pk}}" class="button is-grey is-radiusless">
          <span class="icon is-medium is-size-4"><i class="fas fa-edit"></i></span>
          <span class="is-size-6">Modifier fiche lot</span>
        </router-link>
      </div>
      <div class="column is-narrow" v-if="((!'documents' in lot || !lot.documents || !lot.documents.length) && !lot.plan && !isAdmin) || !displayButton">
        <!-- Fake div/button to keep container height -->
        <div class="button is-static is-radiusless">
          <span class="icon is-medium is-size-4"></span>
          <span class="is-size-6"></span>
        </div>
      </div>
    </div>
    <div class="columns">
      <div class="column is-6">
        <table class="table is-fullwidth is-stripedd is-first-fixed-large is-border-bottom">
          <tr>
            <td class="is-paddingless-left"><span class="title is-4"><b>Détail du lot</b></span></td>
            <td></td>
          </tr>
          <tr>
            <td class="is-paddingless-left"><span class="label">Nom du Programme</span></td>
            <td v-if="program && program.name"><span>{{program.name}}</span></td>
            <td v-else>-</td>
          </tr>
          <tr>
            <td class="is-paddingless-left"><span class="label">Adresse</span></td>
            <td v-if="program && program.address && Object.values(program.address).length">
              <span>{{program.address.line1}} {{program.address.zip_code}} {{program.address.city}} {{program.address.country}}</span>
            </td>
            <td v-else>-</td>
          </tr>
          <tr>
            <td class="is-paddingless-left"><span class="label">Dispositif</span></td>
            <td v-if="taxDispositifs"><span>{{taxDispositifs}}</span></td>
            <td v-else>-</td>
          </tr>
          <tr>
            <td class="is-paddingless-left"><span class="label">Livraison</span></td>
            <td v-if="program && program.date_livraison"><span>{{program.date_livraison | trimestre}}</span></td>
            <td v-else>-</td>
          </tr>
          <tr>
            <td class="is-paddingless-left"><span class="label">{{actabilityTerm}}</span></td>
            <td v-if="program && program.date_actabilite"><span>{{program.date_actabilite | trimestre}}</span></td>
            <td v-else>-</td>
          </tr>
          <tr>
            <td class="is-paddingless-left"><span class="label">Nature</span></td>
            <td v-if="lot && lot.nature"><span>{{natureLabel(lot.nature)}}</span></td>
            <td v-else>-</td>
          </tr>
          <tr>
            <td class="is-paddingless-left"><span class="label">Orientation</span></td>
            <td v-if="lot && lot.exposures && lot.exposures.length"><span>{{lot.exposures | exposuresLabel}}</span></td>
            <td v-else>-</td>
          </tr>
          <tr>
            <td class="is-paddingless-left"><span class="label">Type de logement</span></td>
            <td v-if="lot && lot.lot_type"><span>{{lotTypeLabel(lot.lot_type)}}</span></td>
            <td v-else>-</td>
          </tr>
          <tr>
            <td class="is-paddingless-left"><span class="title is-4"><b>Superficie</b></span></td>
            <td></td>
          </tr>
          <tr>
            <td class="is-paddingless-left"><span class="label"><b>Surface habitable</b></span></td>
            <td v-if="lot &&lot.surface"><span>{{lot.surface | prettyPrice}} m2</span></td>
            <td v-else>-</td>
          </tr>
        </table>
      </div>
      <div class="column is-6">
        <table class="table is-fullwidth is-stripedd is-first-fixed-large is-border-bottom">
          <tr>
            <td class="is-paddingless-left"><span class="title is-4"><b>Annexe(s)</b> TTC</span></td>
            <td></td>
          </tr>
          <tr v-if="lot && lot.annexes && Object.values(lot.annexes).length" v-for="(a, i) in lot.annexes" :key="'annexes' + i">
            <td class="is-paddingless-left">
              <span class="label" v-if="!a.is_parking">{{ a.annex_type | annexTypeLabel | cap }}</span>
              <span class="label" v-else>Parking</span>
            <td>
              <span class="has-text-weight-bold" v-if="a.is_parking">{{ a.annex_type | cap }}</span>
              <span v-if="a.is_parking && (a.surface || a.info)">(</span>
              <span v-if="a.surface">{{ a.surface | prettyPrice}} m2</span>
              <span v-if="a.info">{{ a.info }}</span>
              <span v-if="a.is_parking && (a.surface || a.info)">)</span>
            </td>
          </tr>
          <tr v-else>
            <td class="is-paddingless-left"><span class="title is-4"><b>Pas d'annexe renseignée</b></span></td>
            <td>-</td>
          </tr>
          <tr>
            <td class="is-paddingless-left"><span class="title is-4"><b>Loyer</b> TTC</span></td>
            <td></td>
          </tr>
          <tr>
            <td class="is-paddingless-left"><span class="label">Loyer</span></td>
            <td v-if="lot && lot.rent"><span>{{lot.rent | prettyPrice}} {{$currency}}</span></td>
            <td v-else>-</td>
          </tr>
          <tr v-if="lot && lot.additional_rents && lot.additional_rents.length"
               v-for="(rent, i) in lot.additional_rents" :key="'rent'+i">
            <td class="is-paddingless-left"><span class="label">{{rent.name}}</span></td>
            <td><span>{{rent.rent | prettyPrice }} {{$currency}}</span></td>
          </tr>
          <tr v-else>
            <td class="is-paddingless-left"><span class="label">-</span></td>
            <td>-</td>
          </tr>
        </table>
      </div>          
    </div>
  </div>
</template>

<script>
  import { DefaultValues, DownloadFile, AdvMx } from '@/mixins'
  import { mapState } from 'vuex'
  
  export default {
    name: 'AdvLot',
    mixins: [DefaultValues, DownloadFile, AdvMx],
    props: {
      lot: {
        type: Object,
        default: {}
      },
      program: {
        type: Object,
        default: {}
      },
      classes: {
        type: String
      },
      displayButton: {
        type: Boolean,
        default: true
      }
    },
    computed: {
      ...mapState(['configs']),
      taxDispositifs () {
        if (this.lot && this.lot.tax_dispositifs && this.lot.tax_dispositifs.length) {
          return this.lot.tax_dispositifs.map(x => this.taxDispositifsData[x].label).join(', ')
        }
      },
      actabilityTerm () {
        if (!this.configs) return
        let term = {
          FRA: 'Date d\'actabilité',
          MAR: 'Date du titre foncier'
        }
        return term[this.configs.COUNTRY]
      }
    }
  }
</script>



// WEBPACK FOOTER //
// src/components/adv/Details/AdvLot.vue